import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardEvents } from '../contexts/EventContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getStatusLabel, getStatusBadgeClasses, DB_STATUS } from '../utils/eventStatusMapping';
import StripeConnectCard from '../components/StripeConnectCard';

const { FiCalendar, FiUsers, FiCheckCircle, FiTrendingUp, FiPlus, FiArrowRight } = FiIcons;

const Dashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, role, displayName } = useAuth();
  const { events, leads, loading, error } = useDashboardEvents();
  const debugAuth = import.meta.env.VITE_DEBUG_AUTH === 'true';
  const userId = user?.id ?? null;

  useEffect(() => {
    const stripe = searchParams.get('stripe');
    const message = searchParams.get('message');
    if (stripe === 'success') {
      toast.success('Stripe account connected successfully.');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('stripe');
        next.delete('message');
        return next;
      }, { replace: true });
    } else if (stripe === 'error') {
      toast.error(message ? decodeURIComponent(message) : 'Stripe connection failed. Please try again.');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('stripe');
        next.delete('message');
        return next;
      }, { replace: true });
    } else if (stripe === 'refresh') {
      toast('You can reconnect your Stripe account below if the link expired.');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('stripe');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const userEvents = role === 'student'
    ? events
    : events.filter(event =>
        role === 'admin' || (userId && event.agentId === userId)
      );
  
  const userLeads = leads.filter(lead =>
    role === 'admin' || (userId && lead.agentId === userId)
  );

  const oneOnOneCompleted = userLeads.filter(lead => lead.status === '1_1_completed').length;
  const conversionRate = userLeads.length > 0 
    ? ((userLeads.filter(l => l.status === 'converted').length / userLeads.length) * 100).toFixed(1)
    : 0;

  const stats = [
    {
      title: 'Total Events',
      value: userEvents.length,
      icon: FiCalendar,
      gradient: 'from-blue-500 to-cyan-500',
      change: '+12%',
      bgLight: 'bg-blue-50'
    },
    {
      title: 'Total Leads',
      value: userLeads.length,
      icon: FiUsers,
      gradient: 'from-emerald-500 to-teal-500',
      change: '+18%',
      bgLight: 'bg-emerald-50'
    },
    {
      title: 'Total 1-1 Completed',
      value: oneOnOneCompleted,
      icon: FiCheckCircle,
      gradient: 'from-purple-500 to-pink-500',
      change: '+25%',
      bgLight: 'bg-purple-50'
    },
    {
      title: 'Conversion Rate',
      value: `${conversionRate}%`,
      icon: FiTrendingUp,
      gradient: 'from-orange-500 to-red-500',
      change: '+8%',
      bgLight: 'bg-orange-50'
    }
  ];

  const handleConnectStripe = async () => {
    const accessToken = session?.access_token;
    if (!accessToken) {
      toast.error('Please sign in again to connect your Stripe account.');
      return;
    }
    setStripeConnecting(true);
    try {
      const { url } = await connectStripeAccount(accessToken);
      if (url) {
        window.location.href = url;
        return;
      }
      toast.error('No Stripe link received.');
    } catch (err) {
      toast.error(err?.message || 'Failed to connect Stripe account.');
    } finally {
      setStripeConnecting(false);
    }
  };

  useEffect(() => {
    if (!debugAuth) {
      return;
    }

    const menuItems = role === 'student'
      ? ['Your Registered Events']
      : ['Stats', 'Recent Events', 'Recent Leads'];

    console.groupCollapsed('[Dashboard] render context');
    console.debug('role', role ?? null);
    console.debug('menuItems', menuItems);
    console.groupEnd();
  }, [debugAuth, role]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
        <p className="text-slate-600 font-medium">Loading dashboard data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
        <h2 className="text-lg font-semibold">We couldn&apos;t load your dashboard.</h2>
        <p className="mt-2 text-sm">{error}</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
        <p className="text-slate-600 font-medium">Loading dashboard…</p>
      </div>
    );
  }

  if (role === 'student') {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
              Welcome back, {displayName}!
            </h1>
            <p className="text-slate-600 mt-2">Here are your registered events</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <h2 className="text-xl font-semibold text-slate-900 mb-6">Your Registered Events</h2>
          <div className="space-y-4">
            {userEvents.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiCalendar} className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-600 mb-4">No events registered yet.</p>
                <Link 
                  to="/"
                  className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium"
                >
                  <span>Browse Events</span>
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              userEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border border-slate-200 rounded-xl p-6 hover:shadow-md hover:border-blue-200 transition-all"
                >
                  <h3 className="font-semibold text-slate-900 text-lg mb-2">{event.title}</h3>
                  <p className="text-slate-600 text-sm mb-4">{event.date} at {event.time}</p>
                  <div className="flex space-x-3">
                    <button className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:shadow-lg hover:shadow-blue-500/30 transition-all font-medium">
                      Join Event
                    </button>
                    <button className="px-5 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium">
                      View Details
                    </button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
            Welcome back, {displayName}!
          </h1>
          <p className="text-slate-600 mt-2">Here's what's happening with your events today.</p>
        </div>
        {(role === 'agent' || role === 'admin') && (
          <Link
            to="/dashboard/events/create"
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-5 py-2.5 rounded-lg hover:shadow-lg hover:shadow-blue-500/30 transition-all font-medium"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Create Event</span>
          </Link>
        )}
      </div>

      {(role === 'agent' || role === 'admin') && (
        <StripeConnectCard role={role} userId={userId} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-600 mb-1">{stat.title}</p>
                <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
              </div>
              <div className={`${stat.bgLight} rounded-xl p-3`}>
                <SafeIcon icon={stat.icon} className={`w-6 h-6 bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`} />
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <span className="text-emerald-600 text-sm font-semibold">{stat.change}</span>
              <span className="text-slate-500 text-sm">from last month</span>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Recent Events</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {userEvents.slice(0, 5).map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-4 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{event.title}</p>
                    <p className="text-sm text-slate-600 mt-1">{event.date} • {event.registrations} registered</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                    event.status === DB_STATUS.PUBLIC ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    event.status === DB_STATUS.DRAFT ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    event.status === DB_STATUS.PRIVATE ? 'bg-blue-50 text-blue-700 border-blue-200' :
                    'bg-slate-50 text-slate-700 border-slate-200'
                  }`}>
                    {getStatusLabel(event.status)}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Recent Leads</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {userLeads.slice(0, 5).map((lead, index) => (
                <motion.div
                  key={lead.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between p-4 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{lead.name}</p>
                    <p className="text-sm text-slate-600 mt-1">{lead.email}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    lead.status === 'new' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                    lead.status === 'contacted' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                    lead.status === '1_1_completed' ? 'bg-purple-50 text-purple-700 border border-purple-200' :
                    lead.status === 'converted' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                    'bg-slate-50 text-slate-700 border border-slate-200'
                  }`}>
                    {lead.status === '1_1_completed' ? '1-1 Completed' : lead.status}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
